import { Component, h, Host, Prop, State, Element, getAssetPath } from '@stencil/core';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import { Store } from '../../models/stores/Store';
import { storeService } from '../../utils/services/storeService';
import { GoogleMapsLoader } from '../../utils/services/googleMapsLoader';
import { mapService } from '../../utils/services/mapService';
import { filterService } from '../../utils/services/filterService';
import { loadingBarService } from '../../utils/services/loadingBarService';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { z } from 'zod';
import { ASL_MapInfoWindowProps } from '../aico-store-locator-map-info-window/aico-store-locator-map-info-window';
import { brandService } from '../../utils/services/brandService';
import { TranslationsProps } from '../../utils/services/translationService';


export const ASL_MapProps = z.object({
  enableClustering: z.boolean().optional(),
  isAttached: z.boolean().optional(),
  componentColors: ComponentColors.optional(),
  translationsProps: TranslationsProps.optional(),

  ASL_MapInfoWindowProps: ASL_MapInfoWindowProps.optional(),
});
export type ASL_MapProps = z.infer<typeof ASL_MapProps>;
interface mapState {
  map?: google.maps.Map;
  infoWindow?: google.maps.InfoWindow;
  markersByType: { [key: number]: google.maps.marker.AdvancedMarkerElement[] };
  markerClusters: { [key: number]: MarkerClusterer };
  existingStoreIds: Set<number>;
}

@Component({
  tag: 'aico-store-locator-map',
  styleUrl: 'aico-store-locator-map.css',
  shadow: true,

  assetsDirs: ['../assets']
})
export class AicoStoreLocatorMap {
  private unsubscribeController!: AbortController;
  private mapElement?: HTMLElement;
  private centerChangeTimer: number | null = null;
  private stores: Store[] = [];
  private filteredBrands: number[] = [];
  @State() selectedStore?: Store;
  private mapState: mapState = {
    markersByType: {},
    markerClusters: {},
    existingStoreIds: new Set()
  };
  @Prop() mapId!: string;
  private preventCenterChangeEvent: boolean = false;
  private preventCenterUpdate: boolean = false;
  private targetZoom: number = 15;

  @Prop() enableClustering: boolean = true;
  @Prop() isAttached: boolean = true;
  @Prop() fetchAfterIdleDelayMs: number = 1000;

  @State() center: google.maps.LatLngLiteral = {
    lat: 47.3769,
    lng: 8.5417
  };

  @Prop() componentColors?: ComponentColors;
  @Element() el!: HTMLElement;

  @Prop() props?: ASL_MapProps;

  connectedCallback() {
  }

  componentWillLoad() {
    if (this.props) {
      const parsedProps = ASL_MapProps.parse(this.props);
      this.enableClustering = parsedProps.enableClustering ?? this.enableClustering;
      this.isAttached = parsedProps.isAttached ?? this.isAttached;
      this.componentColors = parsedProps.componentColors ?? this.componentColors;
    }

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }

    this.unsubscribeController?.abort();
    this.unsubscribeController = new AbortController();
  }

  componentDidLoad() {
    this.loadGoogleMaps();
  }

  disconnectedCallback() {
    this.unsubscribeController?.abort();
    this.cleanupMap();
  }

  private panToStore(store: Store) {
    storeService.abortStoreFetch("[aico-store-locator] Store Selected");
    this.selectedStore = store;

    this.center = { lat: this.selectedStore.latitude, lng: this.selectedStore.longitude };
    this.preventCenterChangeEvent = true;
    this.mapState.map?.panTo(this.center);
    const currentZoom = this.mapState.map?.getZoom();
    if (currentZoom! <= this.targetZoom) {
      this.mapState.map?.setZoom(this.targetZoom);
    }
  }

  private loadGoogleMaps() {
    GoogleMapsLoader.getInstance().load()
      .then(async () => {
        await this.initializeMap();
        this.setupSubscribers();
      })
      .catch(err => {
        console.error(err);
      });
  }

  private setupSubscribers() {
    mapService.subscribeToCenter(
      (center: google.maps.LatLngLiteral) => {
        if (this.preventCenterUpdate) {
          this.preventCenterUpdate = false;
          return;
        }
        this.center = center;
        this.mapState.map?.panTo(this.center);
        this.mapState.map?.setZoom(12);
        if (this.isAttached) {
          this.fetchStores();
        }
      }, true, { signal: this.unsubscribeController.signal });
    filterService.subscribe(
      (filters: number[]) => {
        this.filteredBrands = filters;
        this.stores = storeService.getStoresQuery().filterWithoutBrands(filters).sortByDistance().get();
        this.cleanupMap();
        this.updateMarkers(this.stores);
      }, { signal: this.unsubscribeController.signal });
    storeService.subscribeToStores(
      () => {
        this.stores = storeService.getStoresQuery().filterWithoutBrands(this.filteredBrands).sortByDistance().get();
        this.updateMarkers(this.stores);
      }, { signal: this.unsubscribeController.signal });
    storeService.subscribeToSelectedStore(
      (store: Store | undefined) => {
        this.selectedStore = store;
        if (store) {
          this.panToStore(store);
        }
      },
      true,
      { signal: this.unsubscribeController.signal });
  }

  private async initializeMap() {
    if (!this.mapElement) return;

    const { Map } = await google.maps.importLibrary("maps") as google.maps.MapsLibrary;

    const mapOptions: google.maps.MapOptions = {
      mapId: this.mapId,
      center: this.center,
      zoom: 12,

      mapTypeId: google.maps.MapTypeId.ROADMAP,
      disableDefaultUI: !this.isAttached,
      zoomControl: this.isAttached,
      streetViewControl: this.isAttached,
      clickableIcons: false,
      gestureHandling: 'greedy',
    };

    this.mapState.map = new Map(this.mapElement, mapOptions);
    this.mapState.infoWindow = new google.maps.InfoWindow();

    this.setupMapEventListeners();
  }

  private setupMapEventListeners() {
    if (!this.mapState.map) return;

    this.mapState.map.addListener('center_changed', () => {
      if (this.preventCenterChangeEvent) {
        this.preventCenterChangeEvent = false;
        return;
      }
      if (this.centerChangeTimer !== null) {
        window.clearTimeout(this.centerChangeTimer);
      }
      storeService.abortStoreFetch("[aico-store-locator] Map center changed.")

      loadingBarService.setState('pending');
      this.centerChangeTimer = window.setTimeout(() => {
        this.centerChangeTimer = null;
        this.center = this.mapState.map?.getCenter()?.toJSON() || this.center;
        this.preventCenterUpdate = true;
        mapService.setCenter(this.center);
        this.fetchStores();
      }, this.fetchAfterIdleDelayMs);
    });
  }

  private async fetchStores() {
    loadingBarService.setState('loading');

    try {
      await storeService.fetchStores(this.center, this.calculateRadius());
    } catch (err) {
      console.error('Error during map move:', err);
      loadingBarService.setState('error');
    } finally {
      loadingBarService.setState('done');
    }
  }

  private calculateRadius(): number {
    const bounds = this.mapState.map?.getBounds();
    if (!bounds) return 50;

    const center = bounds.getCenter();
    const ne = bounds.getNorthEast();
    return Math.ceil(google.maps.geometry.spherical.computeDistanceBetween(center, ne) / 1000) + 20;
  }

  private updateMarkers(newStores: Store[]) {
    if (!this.mapState.map) return;

    newStores.forEach(store => {
      if (this.mapState.existingStoreIds.has(store.id)) {
        return;
      }
      if (store.crmAddress?.storeLocatorBrands?.length === 0) {
        console.warn(`Store with ID ${store.id} has no brands associated.`);
        return;
      }

      this.mapState.existingStoreIds.add(store.id);
      const marker = this.createMarker(store);
      const iconType = brandService.getHighestPriorityBrand(store?.crmAddress.storeLocatorBrandIds)?.brandId || -1;

      if (!this.mapState.markersByType[iconType]) {
        this.mapState.markersByType[iconType] = [];
      }

      this.mapState.markersByType[iconType].push(marker);

      if (this.enableClustering) {
        if (!this.mapState.markerClusters[iconType]) {
          this.createNewCluster(iconType, [marker]);
        } else {
          this.mapState.markerClusters[iconType].addMarker(marker);
        }
      } else {
        marker.map = this.mapState.map;
      }
    });
  }

  private createMarker(store: Store): google.maps.marker.AdvancedMarkerElement {
    const contentDiv = document.createElement('div');
    const img = document.createElement('img');
    img.src = getAssetPath(brandService.getHighestPriorityBrand(store?.crmAddress.storeLocatorBrandIds)?.pinUrl || '');
    img.alt = "Pin Icon";
    img.className = 'w-6 h-8 text-[16px]';
    contentDiv.appendChild(img);

    const marker = new google.maps.marker.AdvancedMarkerElement({
      position: { lat: store.latitude, lng: store.longitude },
      map: this.enableClustering ? null : this.mapState.map,
      title: store.organisationName,
      content: contentDiv,
    });

    this.setupMarkerClickHandler(marker, store);
    return marker;
  }

  handlePinClick(store: Store) {
    this.selectedStore = store;
    if (store) {
      this.panToStore(store);
      storeService.setSelectedStore(store);
    }
  }

  private setupMarkerClickHandler(marker: google.maps.marker.AdvancedMarkerElement, store: Store) {
    marker.addEventListener('click', () => {
      this.handlePinClick(store);
    });
  }

  private createNewCluster(iconType: number, markers: google.maps.marker.AdvancedMarkerElement[]) {
    this.mapState.markerClusters[iconType] = new MarkerClusterer({
      map: this.mapState.map,
      markers: markers,
      renderer: {
        render: ({ count, position }) => this.createClusterMarker(count, position, iconType)
      },
    });
  }

  private createClusterMarker(count: number, position: google.maps.LatLng, iconType: number): google.maps.marker.AdvancedMarkerElement {
    const div: HTMLElement = document.createElement('div');
    div.className = 'custom-cluster';
    const img = document.createElement('img');
    img.src = getAssetPath(brandService.getBrand(iconType)?.pinUrl || '');
    img.alt = "Cluster Icon";
    img.classList.add('w-6', 'h-8');
    div.innerHTML = `
      <div class="relative text-[16px]">
        ${img.outerHTML}
        <span class="top-0 right-0 absolute bg-[--pin-cluster-background-color] -mt-1 -mr-1 px-1 py-0.5 rounded-full font-bold text-[--pin-cluster-text-color] text-xs">
          ${count}
        </span>
      </div>
    `;

    return new google.maps.marker.AdvancedMarkerElement({
      position,
      content: div,
    });
  }

  private cleanupMap() {
    // Clear markers
    Object.values(this.mapState.markersByType).forEach(markers => {
      markers.forEach(marker => marker.map = null);
    });

    // Clear clusters
    Object.values(this.mapState.markerClusters).forEach(cluster => {
      cluster.clearMarkers();
    });

    // Reset collections
    this.mapState.markerClusters = {};
    this.mapState.markersByType = {};
    this.mapState.existingStoreIds.clear();
  }

  render() {
    return (
      <Host>
        <div class="relative flex p-0 w-full h-full">
          <div ref={el => (this.mapElement = el)} class="w-full h-full" />

          <aico-store-locator-loading-bar />

          <aico-store-locator-map-ledger />
        </div>
      </Host>
    );
  }
}
